import React, { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
} from 'reactstrap';
import ValidatedForm, { ValidatedFormProps } from './ValidatedForm';

type StepDescriptor = {
  title: string;
  helperText?: string;
  inputs: any[];
  isLoading?: boolean;
  renderInputs?: (
    inputs: any[],
    currentObjectRef: MutableRefObject<unknown>
  ) => React.ReactNode;
};

interface Props extends ValidatedFormProps {
  steps: StepDescriptor[];
  onFinish?: () => void;
  onCancel?: () => void;
  stepCallback?: (data: unknown, error: unknown) => void;
}

const ValidatedFormWithSteps: React.FC<Props> = ({
  steps,
  // onFinish,
  onCancel,
  callback,
  stepCallback,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const [currentStep, setCurrentStep] = useState(0);

  const [internalObjectRef, setInternalObjectRef] =
    useState<MutableRefObject<unknown>>();

  const handleReady = useCallback(
    ({ objectRef }: { objectRef: MutableRefObject<unknown> }) => {
      setInternalObjectRef(objectRef);
    },
    []
  );

  const {
    inputs,
    renderInputs,
    title,
    helperText,
    isLoading: isStepLoading,
  } = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = useCallback(
    (data: unknown, error: unknown) => {
      if (!isLastStep) {
        if (stepCallback) {
          stepCallback(data, error);
        }
        setCurrentStep((prev) => prev + 1);
      }
    },
    [isLastStep, stepCallback]
  );

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [isFirstStep]);

  const handleStepClick = useCallback((stepIndex: number) => {
    setCurrentStep(stepIndex);
  }, []);

  const handleNextStep = useCallback(
    (responseData: any, error: unknown) => {
      if (!isLastStep && !error) {
        handleNext(responseData, error);
        return;
      }
      if (!error) {
        callback(responseData, error);
      }
    },
    [callback, handleNext, isLastStep]
  );
  // const handleFinish = useCallback(() => {
  //   if (onFinish) {
  //     onFinish();
  //   }
  // }, [onFinish]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const handleNavClick = useCallback(
    (index: number) => (e: React.MouseEvent) => {
      e.preventDefault();
      handleStepClick(index);
    },
    [handleStepClick]
  );

  const submitTextForStep = useMemo(
    () =>
      isLastStep
        ? formatMessage({
            id: 'app.views.widgets.forms.validated_form_with_steps.save_and_finish',
            defaultMessage: 'Save and Finish',
          })
        : formatMessage({
            id: 'app.views.widgets.forms.validated_form_with_steps.save_and_continue',
            defaultMessage: 'Save and Continue',
          }),
    [isLastStep, formatMessage]
  );

  const loadingButton = useMemo(
    () => (
      <Button color="primary" disabled style={{ position: 'relative' }}>
        <span style={{ visibility: 'hidden' }}>{submitTextForStep}</span>
        <div
          className="spinner-border"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '1rem',
            height: '1rem',
          }}
        />
      </Button>
    ),
    [submitTextForStep]
  );

  const renderForm = useCallback(
    (inputs, submitButton) => {
      return (
        <>
          {inputs}
          <div className="d-flex justify-content-between align-items-center mt-4">
            {!isFirstStep ? (
              <Button className="btn btn-light" onClick={handlePrevious}>
                <FormattedMessage
                  id="app.views.widgets.forms.validated_form_with_steps.back"
                  defaultMessage="Back"
                />
              </Button>
            ) : (
              <div />
            )}

            <div className="d-flex gap-2 align-items-center">
              <Button color="secondary" outline onClick={handleCancel}>
                <FormattedMessage
                  id="app.views.widgets.forms.validated_form_with_steps.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
              {isStepLoading ? loadingButton : submitButton}
            </div>
          </div>
        </>
      );
    },
    [handleCancel, handlePrevious, isFirstStep, isStepLoading, loadingButton]
  );

  const renderInputsWithObject = useCallback(
    (inputs: any[]) => {
      if (internalObjectRef && renderInputs) {
        return renderInputs(inputs, internalObjectRef);
      }
      return undefined;
    },
    [internalObjectRef, renderInputs]
  );

  return (
    <Row>
      <Col md={3} className="mb-4">
        <Nav vertical pills>
          {steps.map((step, index) => (
            <NavItem key={index}>
              <NavLink
                href="#"
                active={currentStep === index}
                onClick={handleNavClick(index)}
                className="d-flex align-items-center"
              >
                <span className="me-2 text-nowrap">{`${index + 1}.`}</span>
                <span className="text-nowrap">{step.title}</span>
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </Col>

      <Col md={9}>
        <Card>
          <CardBody>
            <h3>{title}</h3>
            <p className="text-muted">{helperText}</p>
            <ValidatedForm
              {...props}
              inputs={inputs}
              renderForm={renderForm}
              callback={handleNextStep}
              submitText={submitTextForStep}
              buttonClassName=" "
              renderInputs={renderInputs ? renderInputsWithObject : undefined}
              onReady={handleReady}
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default ValidatedFormWithSteps;
